/**
 * SplitFlapCell.jsx
 *
 * A single cell in the split-flap display that contains multiple characters.
 * Each character can flip independently when the value changes.
 */

import React, { useState, useEffect, useRef } from 'react';

/**
 * Single character display with flip animation
 */
function SplitFlapChar({ char, index, shouldFlip }) {
  const [isFlipping, setIsFlipping] = useState(false);
  const [displayChar, setDisplayChar] = useState(char);
  const prevCharRef = useRef(char);

  useEffect(() => {
    // Only animate if character actually changed
    if (char !== prevCharRef.current && shouldFlip) {
      setIsFlipping(true);

      // Update character mid-flip
      const flipTimer = setTimeout(() => {
        setDisplayChar(char);
      }, 75); // Half of animation duration

      // Clear flip class after animation
      const clearTimer = setTimeout(() => {
        setIsFlipping(false);
      }, 150);

      prevCharRef.current = char;

      return () => {
        clearTimeout(flipTimer);
        clearTimeout(clearTimer);
      };
    } else {
      // No animation, just update
      setDisplayChar(char);
      prevCharRef.current = char;
    }
  }, [char, shouldFlip]);

  return (
    <div
      className={`splitflap-char ${isFlipping ? 'flipping' : ''} ${isFlipping ? `flip-delay-${index % 10}` : ''}`}
    >
      <span className="splitflap-char-text">{displayChar}</span>
    </div>
  );
}

/**
 * Cell containing multiple characters
 *
 * @param {string} value - The string value to display
 * @param {number} width - Number of characters (pads/truncates to fit)
 * @param {string} align - 'left' or 'right' alignment
 * @param {boolean} animate - Whether to animate changes
 * @param {string} statusClass - Additional class for status coloring
 */
export function SplitFlapCell({
  value = '',
  width = 6,
  align = 'left',
  animate = true,
  statusClass = '',
}) {
  // Normalize value: uppercase, replace invalid chars, pad/truncate to width
  const normalizedValue = (value || '')
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9\-:+. ]/g, ' '); // Only allow alphanumeric, dash, colon, plus, dot, space

  // Pad or truncate to exact width
  let displayValue;
  if (align === 'right') {
    displayValue = normalizedValue.padStart(width, ' ').slice(-width);
  } else {
    displayValue = normalizedValue.padEnd(width, ' ').slice(0, width);
  }

  return (
    <div className={`splitflap-cell-container ${statusClass}`}>
      {displayValue.split('').map((char, index) => (
        <SplitFlapChar
          key={index}
          char={char}
          index={index}
          shouldFlip={animate}
        />
      ))}
    </div>
  );
}

export default SplitFlapCell;
